import React, { useState, useEffect } from 'react';

const defaultFormData = {
    title: '',
    description: '',
    listingType: 'sell',
    propertyType: 'other',
    status: 'available',
    city: '',
    locality: '',
    pincode: '',
    price: '',
    area: '',
    areaUnit: 'sqft',
    imageUrls: '',
    ownerName: '',
    ownerPhone: '',
    brokerName: '',
    brokerPhone: '',
    tags: '',
    notes: '',
};

const statusOptions = [
    { value: 'available', label: 'Available' },
    { value: 'hold', label: 'Hold' },
    { value: 'booked', label: 'Booked' },
    { value: 'sold', label: 'Sold' },
    { value: 'inactive', label: 'Inactive' },
];

const typeOptions = [
    { value: 'plot', label: 'Plot' },
    { value: 'flat', label: 'Flat' },
    { value: 'villa', label: 'Villa' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'farm', label: 'Farm' },
    { value: 'other', label: 'Other' },
];

const areaUnitOptions = [
    { value: 'sqft', label: 'sqft' },
    { value: 'sqyd', label: 'sqyd' },
    { value: 'sqm', label: 'sqm' },
    { value: 'acre', label: 'acre' },
];

const parseNumber = (value) => {
    if (value === '' || value === null || value === undefined) return undefined;
    const num = Number(value);
    return Number.isNaN(num) ? undefined : num;
};

function PropertyForm({ property, onSubmit, onCancel, loading }) {
    const [formData, setFormData] = useState(defaultFormData);
    const [errors, setErrors] = useState({});
    const [imageFiles, setImageFiles] = useState([]);

    useEffect(() => {
        if (property) {
            setFormData({
                title: property.title || '',
                description: property.description || '',
                listingType: property.listingType || 'sell',
                propertyType: property.propertyType || 'other',
                status: property.status || 'available',
                city: property.city || '',
                locality: property.locality || '',
                pincode: property.pincode || '',
                price: property.price ?? '',
                area: property.area ?? '',
                areaUnit: property.areaUnit || 'sqft',
                imageUrls: Array.isArray(property.imageUrls) ? property.imageUrls.join('\n') : '',
                ownerName: property.ownerName || '',
                ownerPhone: property.ownerPhone || '',
                brokerName: property.brokerName || '',
                brokerPhone: property.brokerPhone || '',
                tags: (property.tags || []).join(', '),
                notes: property.notes || '',
            });
        } else {
            setFormData(defaultFormData);
        }
        setImageFiles([]);
        setErrors({});
    }, [property]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name]) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    };

    const handleFilesChange = (e) => {
        const files = Array.from(e.target.files || []);
        setImageFiles(files);

        if (errors.imageUrls) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next.imageUrls;
                return next;
            });
        }
    };

    const validate = () => {
        const nextErrors = {};
        if (!formData.title.trim()) {
            nextErrors.title = 'Title is required';
        }
        if (!formData.description.trim()) {
            nextErrors.description = 'Description is required';
        }
        if (!formData.propertyType) {
            nextErrors.propertyType = 'Type is required';
        }
        if (!formData.listingType) {
            nextErrors.listingType = 'Listing is required';
        }
        if (!formData.status) {
            nextErrors.status = 'Status is required';
        }
        if (!formData.city.trim()) {
            nextErrors.city = 'City is required';
        }
        if (!formData.locality.trim()) {
            nextErrors.locality = 'Locality is required';
        }
        if (!formData.pincode.trim()) {
            nextErrors.pincode = 'Pincode is required';
        }
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;

        const tags = formData.tags
            ? formData.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
            : [];
        const imageUrls = formData.imageUrls
            ? formData.imageUrls.split('\n').map((url) => url.trim()).filter(Boolean)
            : [];

        const payload = {
            title: formData.title.trim(),
            description: formData.description.trim(),
            listingType: formData.listingType,
            propertyType: formData.propertyType,
            status: formData.status,
            city: formData.city.trim(),
            locality: formData.locality.trim(),
            pincode: formData.pincode.trim(),
            price: parseNumber(formData.price),
            area: parseNumber(formData.area),
            areaUnit: formData.areaUnit,
            imageUrls,
            ownerName: formData.ownerName.trim(),
            ownerPhone: formData.ownerPhone.trim(),
            brokerName: formData.brokerName.trim(),
            brokerPhone: formData.brokerPhone.trim(),
            tags,
            notes: formData.notes,
            imageFiles,
        };

        onSubmit?.(payload);
    };

    return (
        <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--crm-gray-600)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Property Details
                </h4>
                <div className="crm-form-group">
                    <label className="crm-label required">Title</label>
                    <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        className={`crm-input ${errors.title ? 'crm-input-error' : ''}`}
                        placeholder="e.g., 3BHK Villa in Whitefield"
                    />
                    {errors.title && <p className="crm-error-message">{errors.title}</p>}
                </div>

                <div className="crm-form-group">
                    <label className="crm-label required">Description</label>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className={`crm-textarea ${errors.description ? 'crm-input-error' : ''}`}
                        placeholder="Short description for listing"
                        rows={3}
                    />
                    {errors.description && <p className="crm-error-message">{errors.description}</p>}
                </div>

                <div className="crm-form-row">
                    <div className="crm-form-group">
                        <label className="crm-label required">Type</label>
                        <select
                            name="propertyType"
                            value={formData.propertyType}
                            onChange={handleChange}
                            className={`crm-select ${errors.propertyType ? 'crm-input-error' : ''}`}
                        >
                            {typeOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label required">Listing</label>
                        <select
                            name="listingType"
                            value={formData.listingType}
                            onChange={handleChange}
                            className={`crm-select ${errors.listingType ? 'crm-input-error' : ''}`}
                        >
                            <option value="sell">For Sale</option>
                            <option value="rent">For Rent</option>
                        </select>
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label required">Status</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className={`crm-select ${errors.status ? 'crm-input-error' : ''}`}
                        >
                            {statusOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--crm-gray-600)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Location
                </h4>
                <div className="crm-form-row">
                    <div className="crm-form-group">
                        <label className="crm-label required">City</label>
                        <input
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            className={`crm-input ${errors.city ? 'crm-input-error' : ''}`}
                            placeholder="City"
                        />
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label required">Locality</label>
                        <input
                            type="text"
                            name="locality"
                            value={formData.locality}
                            onChange={handleChange}
                            className={`crm-input ${errors.locality ? 'crm-input-error' : ''}`}
                            placeholder="Locality"
                        />
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label required">Pincode</label>
                        <input
                            type="text"
                            name="pincode"
                            value={formData.pincode}
                            onChange={handleChange}
                            className={`crm-input ${errors.pincode ? 'crm-input-error' : ''}`}
                            placeholder="Pincode"
                        />
                    </div>
                </div>

            </div>

            <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--crm-gray-600)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Pricing & Configuration
                </h4>
                <div className="crm-form-row">
                    <div className="crm-form-group">
                        <label className="crm-label">Price (INR)</label>
                        <input
                            type="number"
                            name="price"
                            value={formData.price}
                            onChange={handleChange}
                            className="crm-input"
                            placeholder="e.g., 8500000"
                            min="0"
                        />
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label">Area</label>
                        <input
                            type="number"
                            name="area"
                            value={formData.area}
                            onChange={handleChange}
                            className="crm-input"
                            placeholder="e.g., 1200"
                            min="0"
                        />
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label">Unit</label>
                        <select
                            name="areaUnit"
                            value={formData.areaUnit}
                            onChange={handleChange}
                            className="crm-select"
                        >
                            {areaUnitOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--crm-gray-600)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Contacts
                </h4>
                <div className="crm-form-row">
                    <div className="crm-form-group">
                        <label className="crm-label">Owner Name</label>
                        <input
                            type="text"
                            name="ownerName"
                            value={formData.ownerName}
                            onChange={handleChange}
                            className="crm-input"
                            placeholder="Owner name"
                        />
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label">Owner Phone</label>
                        <input
                            type="text"
                            name="ownerPhone"
                            value={formData.ownerPhone}
                            onChange={handleChange}
                            className="crm-input"
                            placeholder="Owner phone"
                        />
                    </div>
                </div>

                <div className="crm-form-row">
                    <div className="crm-form-group">
                        <label className="crm-label">Broker Name</label>
                        <input
                            type="text"
                            name="brokerName"
                            value={formData.brokerName}
                            onChange={handleChange}
                            className="crm-input"
                            placeholder="Broker name"
                        />
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label">Broker Phone</label>
                        <input
                            type="text"
                            name="brokerPhone"
                            value={formData.brokerPhone}
                            onChange={handleChange}
                            className="crm-input"
                            placeholder="Broker phone"
                        />
                    </div>
                </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--crm-gray-600)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Images (Recommended For Quality Leads)
                </h4>
                <div className="crm-form-group">
                    <label className="crm-label">Images</label>
                    <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFilesChange}
                        className={`crm-input ${errors.imageUrls ? 'crm-input-error' : ''}`}
                    />
                    {imageFiles.length > 0 && (
                        <p style={{ marginTop: '8px', color: 'var(--crm-gray-500)', fontSize: '12px' }}>
                            Selected: {imageFiles.map((file) => file.name).join(', ')}
                        </p>
                    )}
                    {errors.imageUrls && <p className="crm-error-message">{errors.imageUrls}</p>}
                </div>
                <div className="crm-form-group">
                    <label className="crm-label">Image URLs (optional)</label>
                    <textarea
                        name="imageUrls"
                        value={formData.imageUrls}
                        onChange={handleChange}
                        className="crm-textarea"
                        placeholder="Paste image URLs, one per line"
                        rows={3}
                    />
                </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--crm-gray-600)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Notes & Tags
                </h4>
                <div className="crm-form-group">
                    <label className="crm-label">Tags</label>
                    <input
                        type="text"
                        name="tags"
                        value={formData.tags}
                        onChange={handleChange}
                        className="crm-input"
                        placeholder="e.g., premium, corner, near metro"
                    />
                </div>
                <div className="crm-form-group">
                    <label className="crm-label">Notes</label>
                    <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        className="crm-textarea"
                        placeholder="Add notes about this property..."
                        rows={3}
                    />
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid var(--crm-gray-200)' }}>
                <button
                    type="button"
                    className="crm-btn crm-btn-secondary"
                    onClick={onCancel}
                    disabled={loading}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="crm-btn crm-btn-primary"
                    disabled={loading}
                >
                    {loading ? 'Saving...' : (property ? 'Update Property' : 'Create Property')}
                </button>
            </div>
        </form>
    );
}

export default PropertyForm;
